import { useState } from "react";
import axios from "axios";
import { PhoneCall, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Field, RequiredLegend, FormSummary, focusFirstError, clean, PHONE_ERROR, EMAIL_ERROR } from "./Field";
import { track, checkoutMeta } from "@/lib/metaPixel";
import { API, apiError } from "@/lib/api";

const SLOTS = ["Matin (9h – 12h)", "Midi (12h – 14h)", "Après-midi (14h – 18h)", "Soir (18h – 20h)", "Peu importe"];
const INITIAL = { name: "", phone: "", email: "", slot: SLOTS[4], message: "" };

export const CallbackDialog = () => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");
  const set = (k) => (ev) => {
    setF({ ...f, [k]: ev.target.value });
    if (errors[k]) setErrors(({ [k]: _, ...rest }) => rest);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const e = clean({
      name: f.name.trim().length < 2 && "Le nom est obligatoire.",
      phone: PHONE_ERROR(f.phone),
      email: f.email.trim() && EMAIL_ERROR(f.email),
    });
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError(ev.currentTarget);
    setState("loading");
    setServerError("");
    try {
      const { data } = await axios.post(`${API}/callbacks`, { ...f, email: f.email.trim() || null, ...checkoutMeta(), page_url: window.location.href });
      track("Lead", { content_name: "Demande de rappel", lead_type: "callback" }, data.lead_event_id);
      setState("done");
    } catch (err) {
      setServerError(apiError(err));
      setState("idle");
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && state === "done") { setF(INITIAL); setState("idle"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button type="button" data-testid="callback-open-button" className="btn-ghost-ice">
          <PhoneCall className="w-4 h-4" /> Être rappelé
        </button>
      </DialogTrigger>
      <DialogContent data-testid="callback-dialog" className="sm:max-w-lg rounded-2xl">
        {state === "done" ? (
          <div data-testid="callback-success" className="flex flex-col items-center text-center gap-3 py-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            <DialogTitle className="font-display text-2xl">Demande bien reçue !</DialogTitle>
            <DialogDescription>Merci {f.name.split(" ")[0]}, un conseiller ClimNova vous rappelle au {f.phone} sur le créneau choisi.</DialogDescription>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Être rappelé par un conseiller</DialogTitle>
              <DialogDescription>Une question avant de commander ? Laissez-nous vos coordonnées, nous vous rappelons.</DialogDescription>
              <RequiredLegend testId="callback-required-legend" />
            </DialogHeader>
            <Field id="callback-name" label="Nom" required error={errors.name}>
              <input id="callback-name" data-testid="callback-name-input" value={f.name} onChange={set("name")} aria-invalid={!!errors.name} className="field-input" />
            </Field>
            <Field id="callback-phone" label="Téléphone" required error={errors.phone}>
              <input id="callback-phone" type="tel" data-testid="callback-phone-input" value={f.phone} onChange={set("phone")} aria-invalid={!!errors.phone} placeholder="06 12 34 56 78" className="field-input" />
            </Field>
            <Field id="callback-email" label="E-mail" optional error={errors.email}>
              <input id="callback-email" type="email" data-testid="callback-email-input" value={f.email} onChange={set("email")} aria-invalid={!!errors.email} className="field-input" />
            </Field>
            <Field id="callback-slot" label="Créneau préféré">
              <select id="callback-slot" data-testid="callback-slot-select" value={f.slot} onChange={set("slot")} className="field-input">
                {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field id="callback-message" label="Message" optional>
              <textarea id="callback-message" data-testid="callback-message-input" rows={3} value={f.message} onChange={set("message")} className="field-input resize-none" />
            </Field>
            <FormSummary errors={errors} testId="callback-form-summary" />
            {serverError && <div data-testid="callback-server-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{serverError}</div>}
            <button type="submit" disabled={state === "loading"} data-testid="callback-submit-button" className="btn-heat disabled:opacity-70">
              {state === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer ma demande"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
